import PropTypes from "prop-types";
import React, { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  LineupPanel as AELineupPanel,
  THREE_CARD_OPTIONS,
  shouldShowCaptainIcon,
} from "@ae/lineup";
import { sendPatchesFromDrivePreset } from "@aelive/dds-client";
import GrimsbyTownFCLogo from "../../../assets/images/GrimsbyTownFCLogo.png";
import { getStoredSession } from "../../../utils/sessionManager";
import { dds } from "../../../dds/ddsClient";
import { getEventName, playerKeys } from "../../../utils/playerName";
import { upsertTeamStat } from "../../../utils/liveEventUtils";
import {
  computeOpusCardCounts,
  computeOpusGoalScores,
  computeRedCardTabs,
} from "../../../utils/txLiveHelpers";
import { clockTimeToSeconds } from "../../../utils/fixtureTime";
import CardDuplicateConfirmDialog from "./CardDuplicateConfirmDialog";

const crestPlaceholder = GrimsbyTownFCLogo;

// The lineup library sends cardType as 'yellow1' | 'yellow2' | 'red'
// (see TeamSidePanelOpenDialog handleCardClick). Map both new and
// legacy keys so a 2nd-yellow click reaches double_yellow.
const CARD_TYPE_MAP = {
  red: { event_key: "red_card", event_name: "Red Card" },
  yellow: { event_key: "yellow_card", event_name: "Yellow Card" },
  yellow1: { event_key: "yellow_card", event_name: "Yellow Card" },
  yellow2: { event_key: "double_yellow", event_name: "Double Yellow" },
  secondYellow: { event_key: "double_yellow", event_name: "Double Yellow" },
};

// Reverse lookup: given a real event's event_key, find its CARD_TYPE_MAP
// entry. Needed because the red badge can represent either a red_card or a
// double_yellow event (see cardMap in txLiveHelpers.buildLineup) — when
// removing, the delete payload must describe the event that actually exists,
// not just the nominal type for whichever badge slot was clicked.
const EVENT_KEY_TO_CARD = {
  red_card: CARD_TYPE_MAP.red,
  yellow_card: CARD_TYPE_MAP.yellow,
  double_yellow: CARD_TYPE_MAP.yellow2,
};

// Does this player currently have a logged event for the clicked badge slot?
// Single source of truth for "will this click resolve to a create or a
// delete" — used both by proceedWithCardAction (to actually decide) and by
// handleCardAction's in-flight guard (to decide whether a click landing
// while a delete is still resolving is safe to let through immediately: a
// delete already updates fixtureEvents synchronously/locally before its own
// server round-trip finishes, so if this lookup already finds nothing, the
// next click is unambiguously a fresh create regardless of whether that
// prior delete has fully confirmed server-side yet).
//
// The red badge lights up for BOTH a straight red_card AND a double_yellow
// (2nd yellow = sending-off) — see cardMap in txLiveHelpers.buildLineup,
// which sets cards.red = 1 for double_yellow too. Yellow1 has the same
// problem: a double_yellow event ALSO sets cards.yellow = 2, so a player
// sent off via 2nd yellow shows the yellow1 badge lit with no separate
// yellow_card event ever having existed. Both must also match double_yellow,
// or the lookup finds nothing and falls through to creating a bogus
// duplicate instead of removing the real record.
const findExistingCardEvent = (cardType, card, player, fixtureEvents) => {
  const matchingEventKeys =
    cardType === "red"
      ? ["red_card", "double_yellow"]
      : cardType === "yellow" || cardType === "yellow1"
        ? ["yellow_card", "double_yellow"]
        : [card.event_key];
  return (fixtureEvents || [])
    .filter(
      (e) => e.player1_id === player.id && matchingEventKeys.includes(e.event_key),
    )
    // If a player somehow has more than one (e.g. two yellow_card events
    // before a double-yellow was logged), the most recent one is the live one.
    .sort((a, b) => (b.time_in_seconds ?? 0) - (a.time_in_seconds ?? 0))[0];
};

// Captain-present check for the "No captain assigned" warning banner. Delegates
// to the shared @ae/lineup predicate so the warning ALWAYS agrees with the on-row
// "C" badge (single source of truth — no local mirror to drift).
const isCaptainFlag = (player) => shouldShowCaptainIcon(player);

const LineupPanel = React.memo(
  ({
    teamName = "Grimsby Town",
    crest = crestPlaceholder,
    players = [],
    subs = [],
    accentColor = "#45FFD5",
    logoUrl,
    teamColor,
    competitionId,
    teamId,
    fixtureId,
    seasonId,
    playersListRef: externalPlayersListRef,
    textColor,
    side,
    activeSection,
    onInteract,
    formation,
    staff,
    team_short_name,
    isCopilot,
    // AeDrive-specific card workflow props (used in handleCardAction below)
    fixture,
    clockTime,
    profileId,
    userRole,
    eventService,
    getRendererIdToUse,
    homeTeamId,
    awayTeamId,
    onNotify,
    fixtureEvents,
    onCardIconClick,
    // Needed to mirror StatsPanel's handleRemoveEvent card-delete path
    homeTeam,
    awayTeam,
    updateFixtureState,
    updateRenderer,
    rendererId,
  }) => {
    const [originalPlayerData, setOriginalPlayerData] = useState(null);
    void originalPlayerData;

    // Duplicate-card confirm dialog state, and a Set tracking which player+card
    // presses are currently in flight (submitted but not yet confirmed as
    // logged). A 2nd press for the same player+card while the 1st is still in
    // flight warns via the dialog.
    //
    // Create vs delete settle differently: a delete calls updateFixtureState
    // synchronously (before awaiting the trigger), so fixtureEvents reflects
    // it immediately — its pendingKey can safely clear the moment the trigger
    // call itself settles (see proceedWithCardAction's delete branch). A
    // create has no such synchronous local update — eventService.trigger only
    // acknowledges the write; fixtureEvents doesn't actually contain the new
    // card until the DDS subscription round-trips it back, separately and
    // later. Clearing a create's pendingKey on trigger-settle (like delete
    // does) left a window where inFlightCardPresses said "not pending" while
    // fixtureEvents still didn't have the card — a click in that window (e.g.
    // un-logging right after logging) would look up existingCardEvent, find
    // nothing, and silently create a SECOND duplicate card instead of
    // deleting the first. So a create's pendingKey instead stays set until
    // fixtureEvents actually confirms it (see the effect below), or the
    // fallback timeout gives up — same "wait for the real data" contract
    // pendingCardKeysRef already uses on the @ae/lineup side for its own
    // optimistic-badge tracking.
    const [pendingCardConfirm, setPendingCardConfirm] = useState(null);
    const inFlightCardPresses = useRef(new Set());
    // key -> { playerId, eventKey, timeoutId, generation } for keys added by
    // a CREATE, still awaiting fixtureEvents confirmation (see effect below).
    const pendingCardCreatesRef = useRef({});
    // key -> true while a DELETE for that exact player+card is resolving
    // server-side. A rapid double-click-to-remove landing in that window
    // must be a true no-op (not fall through as a fresh create) — the first
    // click already removed the card, so re-creating it on the 2nd click
    // would silently re-log a card the operator meant to un-log once.
    const pendingCardDeletesRef = useRef({});
    // key -> monotonically increasing counter, bumped every time a NEW
    // create/delete is actually started for that key. handleCardAction can
    // now let a click through while a delete for the same key is still
    // resolving server-side (see there) — meaning two async operations can
    // briefly overlap for one pendingKey. Without this, the OLDER
    // operation's finally block (which fires whenever ITS OWN request
    // settles, regardless of anything newer having since started) would
    // blindly clear inFlightCardPresses/pendingCardCreatesRef out from under
    // the NEWER operation's still-valid tracking. Each operation captures
    // "its" generation when started, and only clears shared state if it's
    // still the current generation for that key — i.e. nothing newer has
    // taken over — so a late-settling stale operation can't stomp on a
    // fresher one.
    const cardOpGenerationRef = useRef({});

    // Clears a create's pendingKey the moment fixtureEvents actually contains
    // the matching event — i.e. once the real data has caught up, not just
    // once the write request itself settled.
    useEffect(() => {
      const pending = pendingCardCreatesRef.current;
      Object.keys(pending).forEach((key) => {
        const { playerId, eventKey, timeoutId, generation } = pending[key];
        const confirmed = (fixtureEvents || []).some(
          (e) => e.player1_id === playerId && e.event_key === eventKey,
        );
        if (!confirmed) return;
        clearTimeout(timeoutId);
        delete pending[key];
        if (cardOpGenerationRef.current[key] === generation) {
          inFlightCardPresses.current.delete(key);
        }
      });
    }, [fixtureEvents]);

    useEffect(() => () => {
      Object.values(pendingCardCreatesRef.current).forEach(({ timeoutId }) => clearTimeout(timeoutId));
    }, []);

    // A lineup with players but nobody flagged captain is a real on-air risk:
    // nothing blocks the operator from triggering graphics without a captain.
    // Surface a persistent warning (rendered in both AeDrive and Copilot, which
    // share this panel) so it can be corrected before going live.
    const needsCaptain = useMemo(() => {
      const all = [...(players || []), ...(subs || [])];
      return all.length > 0 && !all.some(isCaptainFlag);
    }, [players, subs]);

    // Same Aether match-setup deep-link used by the fixture bottom-sheet (Footer
    // → TXLivePage `aetherUrl`): /teamsetup/{competition}/{season}/{fixture}.
    const matchSetupUrl = useMemo(() => {
      const base = process.env.REACT_APP_AETHER_URL;
      if (!base || !competitionId || !seasonId || !fixtureId) return null;
      return `${base.replace(/\/$/, "")}/teamsetup/${competitionId}/${seasonId}/${fixtureId}`;
    }, [competitionId, seasonId, fixtureId]);

    const handlePlayerRightClick = useCallback(
      (player) => {
        if (isCopilot) return;
        if (!player?.id) return;
        const aetherBaseUrl = process.env.REACT_APP_AETHER_URL;
        if (!aetherBaseUrl) {
          console.error(
            "[LineupPanel] REACT_APP_AETHER_URL not configured — cannot deep-link player edit",
          );
          return;
        }
        const seasonId = localStorage.getItem("selectedSeasonId");
        if (!competitionId || !seasonId) {
          console.warn(
            "[LineupPanel] missing competitionId/seasonId for Aether deep-link",
            { competitionId, seasonId },
          );
          return;
        }
        const url =
          `${aetherBaseUrl.replace(/\/$/, "")}` +
          `/collections/${competitionId}/${seasonId}/players` +
          `?openRow=${encodeURIComponent(player.id)}`;
        window.open(url, "_blank", "noopener,noreferrer");
      },
      [competitionId, isCopilot],
    );

    // Coach/staff right-click → Aether staff collection edit, opened in a new tab.
    // Mirrors the player deep-link but targets /staff with the staff_id, and works
    // in BOTH copilot and drive (no isCopilot guard).
    const handleCoachRightClick = useCallback(
      (coach) => {
        const staffId = coach?.staff_id ?? coach?.id;
        if (!staffId) return;
        const aetherBaseUrl = process.env.REACT_APP_AETHER_URL;
        if (!aetherBaseUrl) {
          console.error(
            "[LineupPanel] REACT_APP_AETHER_URL not configured — cannot deep-link coach edit",
          );
          return;
        }
        const seasonIdVal =
          localStorage.getItem("selectedSeasonId") || seasonId;
        if (!competitionId || !seasonIdVal) {
          console.warn(
            "[LineupPanel] missing competitionId/seasonId for Aether coach deep-link",
            { competitionId, seasonId: seasonIdVal },
          );
          return;
        }
        const url =
          `${aetherBaseUrl.replace(/\/$/, "")}` +
          `/collections/${competitionId}/${seasonIdVal}/staff` +
          `?openRow=${encodeURIComponent(staffId)}`;
        window.open(url, "_blank", "noopener,noreferrer");
      },
      [competitionId, seasonId],
    );

    const handleSavePlayer = async (updatedPlayer, originalPlayer) => {
      if (!competitionId || !updatedPlayer) return;

      const session = getStoredSession();
      const addedBy = session?.username || session?.user_name || "Op1";

      const fieldsToCheck = [
        "player_first_name",
        "player_last_name",
        "player_short_name",
        "shirt_number",
        "player_dob",
        "player_birth_place",
        "player_position",
      ];

      const normalizeValue = (value, field) => {
        if (value === null || value === undefined) return "";
        if (field === "shirt_number") return String(value).trim();
        if (typeof value === "number") return String(value);
        return String(value).trim();
      };

      const changedFields = fieldsToCheck.filter((field) => {
        const originalValue = normalizeValue(originalPlayer?.[field], field);
        const updatedValue = normalizeValue(updatedPlayer[field], field);
        return originalValue !== updatedValue;
      });

      if (changedFields.length === 0) return;

      const graphqlEndpoint = dds?.http?.url;
      const seasonIdVal = localStorage.getItem("selectedSeasonId");

      try {
        const patchPromises = changedFields.map(async (field) => {
          const patchId = `P${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${field}`;

          let value = updatedPlayer[field];
          if (field === "shirt_number") {
            value =
              value !== null && value !== undefined && value !== ""
                ? Number(value)
                : null;
          } else if (value === "" || value === null || value === undefined) {
            value = null;
          }

          const mutation = sendPatchesFromDrivePreset({
            collection: "player",
            originSite: session?.username || session?.user_name || "Op1",
            op: "PATCH",
            valid_from: new Date().toISOString(),
            added_by: addedBy,
            patch_id: patchId,
            path: `data.${field}`,
            value,
            extraKey: {
              competition_id: competitionId,
              player_id: updatedPlayer.id,
              team_id: teamId,
              season_id: seasonIdVal,
            },
          });

          const response = await fetch(graphqlEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              query: mutation.query,
              variables: mutation.variables,
            }),
          });

          if (!response.ok)
            throw new Error(
              `HTTP error! status: ${response.status} for field ${field}`,
            );
          const result = await response.json();
          if (result.errors)
            throw new Error(result.errors.map((e) => e.message).join(", "));
          return result.data;
        });

        await Promise.all(patchPromises);
      } catch (error) {
        throw error;
      }
    };

    // Un-setting a card badge on the Lineup panel — mirrors StatsPanel's
    // handleRemoveEvent "isCard" branch so a card removed here is deleted the
    // same way a card removed from the Timeline is (same eventService.trigger
    // shape, same local fixtureEvents cleanup), instead of only clearing the
    // badge locally and leaving the event behind in the Timeline.
    const handleCardRemove = useCallback(
      async (teamSide, player, card, teamIdForCard, existingCardEvent) => {
        const eventId = existingCardEvent.event_id;

        updateFixtureState?.({
          collection: "event",
          op: "delete",
          key: { fixture_id: fixtureId, event_id: eventId },
          data: { event_id: eventId },
        });

        const eventsAfter = (fixtureEvents || []).filter(
          (e) => e.event_id !== eventId,
        );
        const scores = computeOpusGoalScores(
          eventsAfter,
          homeTeam?.team_id,
          awayTeam?.team_id,
        );
        const redCardTabs = computeRedCardTabs(
          eventsAfter,
          homeTeam?.team_id,
          awayTeam?.team_id,
        );

        // Stat upsert failures shouldn't block the actual event deletion below
        // (mirrors handleCardAction's create path, which isolates this the
        // same way).
        try {
          const { yellow, red } = computeOpusCardCounts(
            eventsAfter,
            teamIdForCard,
          );
          await Promise.all([
            upsertTeamStat({
              fixtureId,
              competitionId,
              seasonId,
              teamId: teamIdForCard,
              statType: "totalYellowCardOpus",
              value: yellow,
            }),
            upsertTeamStat({
              fixtureId,
              competitionId,
              seasonId,
              teamId: teamIdForCard,
              statType: "totalRedCardOpus",
              value: red,
            }),
          ]);
        } catch (e) {
          console.error("Failed to upsert Opus card stats", e);
        }

        try {
          const triggerResult = await eventService?.trigger({
            type: "card",
            event_op: "delete",
            event_key: card.event_key,
            card_type: card.event_key,
            event_id: eventId,
            delete_reason: "manual",
            fixtureId,
            competitionId,
            seasonId,
            team_id: teamIdForCard,
            player1_id: player.id,
            ...playerKeys(player),
            time_in_seconds: existingCardEvent.time_in_seconds,
            game_period:
              existingCardEvent.game_period ?? fixture?.fixture_session ?? null,
            team_side: teamSide,
            homeTeam,
            awayTeam,
            scores,
            redCardTabs,
            fixtureEvents: fixtureEvents || [],
            rendererId: getRendererIdToUse ? getRendererIdToUse() : rendererId,
            profile_id: profileId,
            user_role: userRole,
          });
          for (const cmd of triggerResult?.rendererCommands ?? []) {
            try {
              await updateRenderer?.(cmd);
            } catch (e) {
              console.error("Failed renderer cmd on card delete", e);
            }
          }
        } catch (e) {
          console.error("Failed to delete card event", e);
        }

        onNotify?.(
          `${card.event_name} removed for ${getEventName(player)}.`,
          "success",
        );
      },
      [
        fixtureId,
        competitionId,
        seasonId,
        homeTeam,
        awayTeam,
        fixture,
        eventService,
        profileId,
        userRole,
        getRendererIdToUse,
        rendererId,
        updateFixtureState,
        updateRenderer,
        fixtureEvents,
        onNotify,
      ],
    );

    const proceedWithCardAction = useCallback(
      async (teamSide, player, cardType, pendingKey, generation, forceCreate = false) => {
        const timeInSeconds = clockTimeToSeconds(clockTime);
        const card = CARD_TYPE_MAP[cardType] ?? CARD_TYPE_MAP.yellow;
        const teamIdForCard = teamSide === "home" ? homeTeamId : awayTeamId;

        // @ae/lineup now calls onCardUpdate on BOTH setting and un-setting a
        // card badge (previously un-setting was swallowed internally, so a
        // card removed from the Lineup never got deleted here — it stayed
        // visible in the Timeline, which reads the same fixtureEvents). There's
        // no separate "removing" flag on the callback, so figure out which one
        // this is by checking whether a matching event already exists.
        //
        // forceCreate skips this toggle-detection entirely. It's set only by
        // the "Possible duplicate" confirm dialog's Continue button: by the
        // time an operator answers that prompt, the create it warned about
        // may have already landed and be sitting in fixtureEvents, which
        // would make this lookup find it and mis-read Continue as "delete
        // the card that was just logged" instead of "yes, log it anyway".
        const existingCardEvent =
          !forceCreate && findExistingCardEvent(cardType, card, player, fixtureEvents);

        if (existingCardEvent) {
          // Delete whatever the event actually is (red_card or double_yellow),
          // not the nominal key for the clicked badge slot — they can differ
          // for the red badge as noted above.
          const cardToRemove =
            EVENT_KEY_TO_CARD[existingCardEvent.event_key] ?? card;
          // Mark BEFORE the first await so a rapid 2nd click on this exact
          // player+card lands with the flag already set (see
          // pendingCardDeletesRef / handleCardAction's isDeletePending check).
          if (pendingKey) {
            pendingCardDeletesRef.current[pendingKey] = true;
          }
          try {
            await handleCardRemove(
              teamSide,
              player,
              cardToRemove,
              teamIdForCard,
              existingCardEvent,
            );
          } finally {
            // handleCardRemove's updateFixtureState call (inside it) is
            // synchronous and already ran before this awaits its trigger
            // call, so fixtureEvents is accurate by the time we get here —
            // unlike a create (see pendingCardCreatesRef above), a delete's
            // pendingKey can safely clear the moment the write settles.
            //
            // Without this, this pendingKey stayed in inFlightCardPresses
            // forever after an un-log (this branch used to `return` before
            // ever reaching cleanup), so the NEXT attempt to log the same
            // card for this player always hit the "already being logged"
            // duplicate-confirm dialog even though nothing was in flight.
            //
            // Only clear if nothing NEWER has since taken over this key —
            // handleCardAction can now let a create through while this
            // delete is still resolving (see there), so by the time this
            // await settles, a fresher operation may already own this key.
            // Clearing unconditionally would stomp on that fresher one's
            // still-valid tracking.
            if (pendingKey && cardOpGenerationRef.current[pendingKey] === generation) {
              inFlightCardPresses.current.delete(pendingKey);
              delete pendingCardDeletesRef.current[pendingKey];
              // Defensive: also drop any lingering create-confirmation watch
              // for this same key (e.g. a create's fixtureEvents confirmation
              // hadn't fired yet when this delete raced in and won) so a stale
              // entry can't keep pendingCardCreatesRef watching for an event
              // that's now gone.
              if (pendingCardCreatesRef.current[pendingKey]) {
                clearTimeout(pendingCardCreatesRef.current[pendingKey].timeoutId);
                delete pendingCardCreatesRef.current[pendingKey];
              }
            }
          }
          return;
        }

        // Register this as a pending create IMMEDIATELY — synchronously,
        // before anything else in this branch runs, not only after the
        // request succeeds. handleCardAction's `isCreatePending` check reads
        // this same map to decide whether a click landing while this request
        // is still in-flight (not yet resolved, which can take several
        // seconds) is safe to let through. Registering only on success left
        // a real gap: for the whole time this create was in progress —
        // including this function's OWN earlier awaits below (the team-stat
        // upserts), not just eventService.trigger — `isCreatePending` read
        // false, so a repeat click on the same badge sailed through the
        // "safe, unambiguous" path and fired ANOTHER create — no popup,
        // multiple duplicate card events. Because proceedWithCardAction runs
        // synchronously up to its first `await` even when the caller
        // (handleCardAction) doesn't await it, placing this registration
        // before ANY await in this branch (not just before
        // eventService.trigger) makes it visible to the very next click,
        // however soon it lands.
        if (pendingKey) {
          pendingCardCreatesRef.current[pendingKey] = {
            playerId: player.id,
            eventKey: card.event_key,
            timeoutId: null,
            generation,
          };
        }

        // Don't write the card event directly here — eventService.trigger
        // below loads card_event_object_builder.js, which returns a bundle
        // containing the liveEvent. The eventService dispatcher routes that
        // through dds.sendLiveEvent. Calling sendLiveEvent here too produced
        // two card writes per click (UI showed both yellow1 and yellow2).

        try {
          const nextEvents = [
            ...(fixtureEvents || []),
            { event_key: card.event_key, team_id: teamIdForCard },
          ];
          const { yellow, red } = computeOpusCardCounts(
            nextEvents,
            teamIdForCard,
          );
          await Promise.all([
            upsertTeamStat({
              fixtureId,
              competitionId,
              seasonId,
              teamId: teamIdForCard,
              statType: "totalYellowCardOpus",
              value: yellow,
            }),
            upsertTeamStat({
              fixtureId,
              competitionId,
              seasonId,
              teamId: teamIdForCard,
              statType: "totalRedCardOpus",
              value: red,
            }),
          ]);
        } catch (e) {
          console.error("Failed to upsert Opus card stats", e);
        }

        let createSucceeded = false;
        try {
          await eventService.trigger({
            type: "card",
            event_op: "create",
            fixtureId,
            competitionId,
            seasonId,
            renderer: getRendererIdToUse?.(),
            team_id: teamIdForCard,
            player1_id: player.id,
            player_name: player.player_last_name ?? player.name ?? null,
            shirt_number: player.shirt_number ?? null,
            ...playerKeys(player),
            time_in_seconds: timeInSeconds,
            game_period: fixture?.fixture_session ?? null,
            team_side: teamSide,
            card_type: card.event_key,
            fixtureEvents: fixtureEvents || [],
            profile_id: profileId,
            user_role: userRole,
          });
          createSucceeded = true;
        } catch (e) {
          console.error("Failed to persist card event", e);
        } finally {
          // Only act if nothing newer has since taken over this key (see
          // cardOpGenerationRef) — a stale, still-settling operation must
          // not clobber a fresher one's tracking.
          if (pendingKey && cardOpGenerationRef.current[pendingKey] === generation) {
            if (createSucceeded) {
              // Stay "in flight" until fixtureEvents actually confirms this
              // card (see the confirmation effect near inFlightCardPresses'
              // declaration) instead of clearing right here — clearing on
              // trigger-settle alone left a window where fixtureEvents still
              // didn't have the new card yet, so a fast follow-up click (e.g.
              // un-logging right after logging) could race ahead of the DDS
              // subscription, find no existing event, and silently create a
              // duplicate instead of deleting. Fallback timeout guards
              // against the subscription never confirming (lost message,
              // etc.) so this can't get stuck forever.
              const timeoutId = setTimeout(() => {
                if (cardOpGenerationRef.current[pendingKey] === generation) {
                  delete pendingCardCreatesRef.current[pendingKey];
                  inFlightCardPresses.current.delete(pendingKey);
                }
              }, 10000);
              if (pendingCardCreatesRef.current[pendingKey]) {
                pendingCardCreatesRef.current[pendingKey].timeoutId = timeoutId;
              }
            } else {
              // The write itself failed — nothing to wait for, and nothing
              // to keep watching fixtureEvents for.
              delete pendingCardCreatesRef.current[pendingKey];
              inFlightCardPresses.current.delete(pendingKey);
            }
          }
        }

        const cardText =
          cardType === "red"
            ? "Red Card"
            : cardType === "secondYellow" || cardType === "yellow2"
              ? "2nd Yellow Card"
              : "Yellow Card";

        onNotify?.(
          `${cardText} logged for ${getEventName(player)}.`,
          "success",
        );

        // After logging, show the card graphic. Copilot now routes through the Maestro
        // "Cards" caption (onCardIconClick); other profiles keep the F3 / "Clock Cards"
        // shortcut fallback.
        if (onCardIconClick) {
          onCardIconClick();
        } else {
          setTimeout(() => {
            const buttons = Array.from(document.querySelectorAll("button"));
            const targetBtn = buttons.find(
              (b) =>
                b.textContent.includes("Clock Cards") ||
                (b.textContent.includes("Cards") &&
                  b.textContent.includes("F3")),
            );
            if (targetBtn) {
              targetBtn.click();
              return;
            }
            document.body.dispatchEvent(
              new KeyboardEvent("keydown", {
                key: "F3",
                code: "F3",
                keyCode: 114,
                which: 114,
                shiftKey: true,
                bubbles: true,
              }),
            );
          }, 50);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        fixtureId,
        competitionId,
        seasonId,
        homeTeamId,
        awayTeamId,
        clockTime,
        eventService,
        fixture,
        profileId,
        userRole,
        getRendererIdToUse,
        onNotify,
        fixtureEvents,
        onCardIconClick,
        handleCardRemove,
      ],
    );

    // Guard in front of proceedWithCardAction: if this exact player+card is
    // already mid-write (see inFlightCardPresses above), warn with
    // Continue/Cancel instead of silently firing a 2nd write. Ops can
    // fat-finger these buttons — these aren't automated writes — so we confirm
    // rather than block outright.
    //
    // Returns true/false (accepted/rejected) synchronously so @ae/lineup's
    // TeamSidePanelOpenDialog can defer ITS optimistic badge toggle to this
    // answer, instead of deciding independently from its own separate
    // pending-tracking ref. Two independently-tracked "is this in flight"
    // refs could disagree — e.g. the library's own ref had already cleared
    // (so it optimistically flipped the badge) while inFlightCardPresses
    // here still considered the card in flight (still showing this popup),
    // so the actual delete/create was never sent — the badge changed but the
    // underlying event didn't. Making this return value authoritative closes
    // that gap: the badge can now only change when this function actually
    // proceeds.
    const handleCardAction = useCallback(
      (teamSide, player, cardType) => {
        const card = CARD_TYPE_MAP[cardType] ?? CARD_TYPE_MAP.yellow;
        const pendingKey = player?.id ? `${player.id}:${card.event_key}` : null;

        // A double yellow is only meaningful once a first yellow is already
        // on the player — logging one straight from a clean sheet would
        // sending someone off who's only ever seen one card. Only guard the
        // CREATE direction (findExistingCardEvent finds nothing yet): un-logging
        // an already-created double yellow must go through regardless.
        if (
          card.event_key === "double_yellow" &&
          !findExistingCardEvent(cardType, card, player, fixtureEvents) &&
          !(fixtureEvents || []).some(
            (e) => e.player1_id === player.id && e.event_key === "yellow_card",
          )
        ) {
          setPendingCardConfirm({
            title: "Missing first yellow",
            message: `${getEventName(player)} hasn't been logged with a first yellow card yet.`,
          });
          return false;
        }

        if (pendingKey && inFlightCardPresses.current.has(pendingKey)) {
          // A create still awaiting fixtureEvents confirmation is always a
          // hard block — that's the genuine duplicate-creation race.
          //   - If a delete for this exact player+card is still resolving
          //     server-side, ignore this click outright — no popup, no
          //     fresh create. The 1st click already removed the card; a
          //     rapid 2nd click landing before that settles must be a true
          //     no-op, not a silent re-log of the card the operator just
          //     un-logged.
          //   - Otherwise, this click would resolve to ANOTHER delete for
          //     the same record — block that (rapid accidental
          //     double-un-log) with the duplicate-confirm popup, same as
          //     the create case.
          const isCreatePending = !!pendingCardCreatesRef.current[pendingKey];
          const isDeletePending = !isCreatePending && !!pendingCardDeletesRef.current[pendingKey];
          const wouldAlsoDelete =
            !isCreatePending && !isDeletePending && !!findExistingCardEvent(cardType, card, player, fixtureEvents);
          if (isCreatePending || wouldAlsoDelete) {
            setPendingCardConfirm({
              title: "Possible duplicate",
              message: `${getEventName(player)}'s ${card.event_name} is already being logged.`,
            });
            return false;
          }
          if (isDeletePending) {
            return false;
          }
          // Falls through: nothing in flight for this key anymore — safe,
          // unambiguous create — proceed below as normal.
        }

        let generation;
        if (pendingKey) {
          inFlightCardPresses.current.add(pendingKey);
          generation = (cardOpGenerationRef.current[pendingKey] || 0) + 1;
          cardOpGenerationRef.current[pendingKey] = generation;
        }
        proceedWithCardAction(teamSide, player, cardType, pendingKey, generation);
        return true;
      },
      [proceedWithCardAction, fixtureEvents],
    );

    // Continue no longer force-creates the duplicate card event — it's a
    // dead end just like Cancel, since forcing it through was the actual bug
    // (OPUS-1322/1112): a fat-fingered double-click on Continue could still
    // fire a genuine duplicate. Both buttons just dismiss the warning now.
    const handleCardConfirmClose = useCallback(
      () => setPendingCardConfirm(null),
      [],
    );
    const handleCardConfirmProceed = useCallback(() => {
      if (!pendingCardConfirm) return;
      const { teamSide, player, cardType } = pendingCardConfirm;
      setPendingCardConfirm(null);
      const card = CARD_TYPE_MAP[cardType] ?? CARD_TYPE_MAP.yellow;
      const pendingKey = player?.id ? `${player.id}:${card.event_key}` : null;
      let generation;
      if (pendingKey) {
        inFlightCardPresses.current.add(pendingKey);
        generation = (cardOpGenerationRef.current[pendingKey] || 0) + 1;
        cardOpGenerationRef.current[pendingKey] = generation;
      }
      // forceCreate: true — Continue always means "log it anyway", never
      // "delete the one that just landed" (see proceedWithCardAction).
      proceedWithCardAction(teamSide, player, cardType, pendingKey, generation, true);
    }, [pendingCardConfirm, proceedWithCardAction]);

    return (
      <>
        {/* `draggable` is deliberately not passed. @ae/lineup renders its own
            bottom-centre Snackbar, and that Snackbar lives entirely inside the
            component's `if (draggable)` branch — so with the default (false) it
            never mounts. OPUS-1527 forbids overlay toasts in Drive, so turning
            drag on here first needs an @ae/lineup release that routes those
            messages out through a callback instead of rendering its own. */}
        <AELineupPanel
          teamName={teamName}
          crest={crest}
          logoUrl={logoUrl}
          teamLogo={logoUrl || crest}
          // Fallback crest if the primary logo src fails to load.
          fallbackTeamLogo={crest || GrimsbyTownFCLogo}
          teamColor={teamColor || accentColor}
          accentColor={accentColor}
          textColor={textColor}
          players={players}
          subs={subs}
          staff={staff}
          competitionId={competitionId}
          teamId={teamId}
          fixtureId={fixtureId}
          playersListRef={externalPlayersListRef}
          side={side}
          formation={formation}
          team_short_name={team_short_name}
          isCopilot={isCopilot}
          // AeDrive isn't the copilot on-pitch reclassification (isCopilot), so the
          // sub icon needs its own read-only signal — showSubStatus drives it off
          // each row's isOnPitch vs is_starting (see @ae/lineup shouldShowSubIcon).
          // Always on here: AeDrive always wants to see who's actually been subbed.
          showSubStatus
          bottomWarning
          cardOptions={THREE_CARD_OPTIONS}
          cardDialogAccentColor={accentColor}
          cardDialogVariant="aedrive"
          activeSection={activeSection}
          onInteract={onInteract}
          onPlayerSave={(updated, original) => {
            setOriginalPlayerData(original);
            handleSavePlayer(updated, original);
          }}
          onPlayerRightClick={handlePlayerRightClick}
          onCoachRightClick={handleCoachRightClick}
          onCardUpdate={(player, cardType) =>
            handleCardAction(side, player, cardType)
          }
        />

        {needsCaptain && !isCopilot && (
          <Box
            component={matchSetupUrl ? "a" : "div"}
            href={matchSetupUrl || undefined}
            target={matchSetupUrl ? "_blank" : undefined}
            rel={matchSetupUrl ? "noopener noreferrer" : undefined}
            sx={{
              mt: 0.75,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.5,
              px: 1,
              py: 0.4,
              borderRadius: 1,
              textDecoration: "none",
              bgcolor: "rgba(254, 236, 103, 0.10)",
              border: "1px solid rgba(254, 236, 103, 0.30)",
              cursor: matchSetupUrl ? "pointer" : "default",
              transition: "background-color 0.15s ease",
              "&:hover": matchSetupUrl
                ? { bgcolor: "rgba(254, 236, 103, 0.18)" }
                : {},
            }}
          >
            <WarningAmberRoundedIcon
              sx={{ fontSize: 13, color: "#FEEC67", flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontSize: 11,
                lineHeight: 1.2,
                fontWeight: 600,
                color: "#FEEC67",
              }}
            >
              No captain assigned
            </Typography>
            {matchSetupUrl && (
              <>
                <Typography
                  sx={{
                    fontSize: 11,
                    lineHeight: 1.2,
                    color: "rgba(254, 236, 103, 0.5)",
                  }}
                >
                  —
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    lineHeight: 1.2,
                    fontWeight: 600,
                    color: "#FEEC67",
                    textDecoration: "underline",
                  }}
                >
                  Open match setup
                </Typography>
                <OpenInNewIcon
                  sx={{ fontSize: 12, color: "#FEEC67", flexShrink: 0 }}
                />
              </>
            )}
          </Box>
        )}

        <CardDuplicateConfirmDialog
          open={!!pendingCardConfirm}
          title={pendingCardConfirm?.title}
          message={pendingCardConfirm?.message}
          onClose={handleCardConfirmClose}
        />
      </>
    );
  },
);

LineupPanel.propTypes = {
  teamName: PropTypes.string,
  crest: PropTypes.string,
  players: PropTypes.array,
  subs: PropTypes.array,
  staff: PropTypes.array,
  accentColor: PropTypes.string,
  logoUrl: PropTypes.string,
  teamColor: PropTypes.string,
  competitionId: PropTypes.string,
  teamId: PropTypes.string,
  fixtureId: PropTypes.string,
  seasonId: PropTypes.string,
  playersListRef: PropTypes.shape({ current: PropTypes.object }),
  textColor: PropTypes.string,
  side: PropTypes.string,
  activeSection: PropTypes.string,
  onInteract: PropTypes.func,
  formation: PropTypes.string,
  team_short_name: PropTypes.string,
  isCopilot: PropTypes.bool,
  fixture: PropTypes.object,
  clockTime: PropTypes.string,
  profileId: PropTypes.string,
  userRole: PropTypes.string,
  eventService: PropTypes.object,
  getRendererIdToUse: PropTypes.func,
  homeTeamId: PropTypes.string,
  awayTeamId: PropTypes.string,
  onNotify: PropTypes.func,
  fixtureEvents: PropTypes.array,
  onCardIconClick: PropTypes.func,
  homeTeam: PropTypes.object,
  awayTeam: PropTypes.object,
  updateFixtureState: PropTypes.func,
  updateRenderer: PropTypes.func,
  rendererId: PropTypes.string,
};

export default LineupPanel;
